import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as usersApi from "./api";
import type { UserFilters } from "./api";

const KEY = "users";

export function useUsers(filters: UserFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    // Filters are part of the key so paging and searching each get their own
    // cache entry instead of clobbering one another.
    queryKey: [KEY, filters],
    queryFn: () => usersApi.listUsers(filters),
    placeholderData: (prev) => prev, // avoids a flash of empty table while paging
    // Callers without user:read must be able to skip the request entirely.
    // Passing empty filters is not enough — the query still fires and 403s.
    enabled: options.enabled ?? true,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: usersApi.listRoles,
    staleTime: 5 * 60_000, // roles change rarely
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ["departments"],
    queryFn: usersApi.listDepartments,
    staleTime: 5 * 60_000,
  });
}


function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY] });
    void qc.invalidateQueries({ queryKey: ["departments"] }); // user_count moves
  };
}

export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: usersApi.createUser,
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof usersApi.updateUser>[1] }) =>
      usersApi.updateUser(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: usersApi.deleteUser,
    onSuccess: invalidate,
  });
}

function useInvalidateDepartments() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["departments"] });
}

export function useCreateDepartment() {
  const invalidate = useInvalidateDepartments();
  return useMutation({ mutationFn: usersApi.createDepartment, onSuccess: invalidate });
}

export function useDeleteDepartment() {
  const invalidate = useInvalidateDepartments();
  return useMutation({ mutationFn: usersApi.deleteDepartment, onSuccess: invalidate });
}

export function useResetPassword() {
  return useMutation({ mutationFn: usersApi.resetPassword });
}

export function useSetPassword() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof usersApi.setPassword>[1] }) =>
      usersApi.setPassword(id, body),
    // Refresh so the "password reset pending" badge reflects the new state.
    onSuccess: invalidate,
  });
}
